import React from 'react';
import { ArticleLayout } from '../../components/ArticleLayout';

export const GoogleSheetsXliffArticle: React.FC = () => {
    return (
        <ArticleLayout
            title="How to Convert Google Sheets to XLIFF for Localization"
            subtitle="The complete guide to preparing Google Sheets for professional translation with CAT tools."
        >
            <h3>Why Use Google Sheets for Localization?</h3>
            <p>
                Google Sheets has become a popular choice for managing localization content. Teams love it for
                real-time collaboration, easy sharing, and zero software installation. But when it's time to
                actually translate that content, you need a way to get it into professional CAT tools like
                Trados, MemoQ, or Phrase.
            </p>
            <p>
                That's where XLIFF comes in. XLIFF (XML Localization Interchange File Format) is the industry
                standard for exchanging translation data between tools. Converting your Google Sheets to XLIFF
                unlocks powerful features like translation memory, terminology management, and quality assurance.
            </p>

            <h3>The Traditional (Painful) Workflow</h3>
            <p>
                Before tools like PolyGrid, converting Google Sheets to XLIFF meant:
            </p>
            <ol>
                <li>Downloading your Google Sheet as Excel (.xlsx)</li>
                <li>Opening it in a desktop tool or writing custom scripts</li>
                <li>Manually mapping columns to source/target languages</li>
                <li>Hoping the formatting survives the conversion</li>
                <li>Repeating this every time the sheet changes</li>
            </ol>
            <p>
                This process is tedious, error-prone, and doesn't scale well for teams managing multiple languages.
            </p>

            <h3>The Better Way: PolyGrid</h3>
            <p>
                PolyGrid streamlines this entire workflow. You have two options:
            </p>

            <h4>Option 1: Use Our Google Sheets Add-on</h4>
            <p>
                Install the PolyGrid add-on directly in Google Sheets. With one click from the sidebar,
                your spreadsheet is exported and opened in PolyGrid, ready for configuration. No downloads,
                no file juggling.
            </p>
            <ul>
                <li>Click "Convert to XLIFF" in the sidebar</li>
                <li>PolyGrid opens with your sheet pre-loaded</li>
                <li>Configure your source and target columns</li>
                <li>Download CAT-tool-ready XLIFF files</li>
            </ul>

            <h4>Option 2: Download and Upload</h4>
            <p>
                Prefer to work manually? Download your Google Sheet as .xlsx, then drag and drop it into
                PolyGrid's web interface. Same powerful conversion, just a different starting point.
            </p>

            <h3>Key Features for Google Sheets Users</h3>
            <ul>
                <li><strong>Multi-language Support:</strong> Handle sheets with multiple target language columns in one conversion.</li>
                <li><strong>Smart Segmentation:</strong> Split sentences properly for better translation memory matches.</li>
                <li><strong>HTML Preservation:</strong> Keep your formatting tags intact through the round-trip.</li>
                <li><strong>Merge Back:</strong> After translation, merge XLIFF files back into your original sheet structure.</li>
            </ul>

            <h3>Best Practices for Google Sheets Localization</h3>
            <p>
                To get the best results when converting Google Sheets to XLIFF:
            </p>
            <ul>
                <li><strong>Use a consistent structure:</strong> Keep your key column in column A, source text in B, and targets in subsequent columns.</li>
                <li><strong>Label your headers clearly:</strong> Use language codes (en, es, fr) or full names (English, Spanish, French) in your header row.</li>
                <li><strong>Avoid merged cells:</strong> They don't translate well to XLIFF format.</li>
                <li><strong>Keep one sheet per context:</strong> Separate UI strings, marketing copy, and documentation into different sheets.</li>
            </ul>

            <h3>Compatible CAT Tools</h3>
            <p>
                XLIFF files generated from your Google Sheets work with all major translation tools:
            </p>
            <ul>
                <li>SDL Trados Studio</li>
                <li>MemoQ</li>
                <li>Phrase (formerly Memsource)</li>
                <li>Smartcat</li>
                <li>Wordfast</li>
                <li>OmegaT</li>
                <li>Any tool supporting XLIFF 1.2 or 2.1</li>
            </ul>

            <h3>Getting Started</h3>
            <p>
                Ready to streamline your Google Sheets localization workflow? Head to the converter and
                try it with your own spreadsheet. Free accounts can convert up to 200 rows per file with
                up to 4 target languages.
            </p>
        </ArticleLayout>
    );
};
